/**
 * /admin/suscripciones — Gestionar Planes de Suscripción
 *
 * Propósito dual:
 *   1. Setup inicial de un nuevo brand — configura los planes de pago para cada nivel.
 *   2. Gestión continua — actualiza, agrega o vincula planes existentes en Stripe / MercadoPago.
 *
 * Los niveles (basic, standard, vip, platinum) son fijos en el sistema.
 * Los planes de pago (stripe_price_id / mercadopago_plan_id) son específicos de cada brand.
 *
 * Stripe y MercadoPago son completamente independientes:
 *   - Stripe  → USD · internacional → campo stripe_price_id  en membership_products
 *   - MP      → ARS · Argentina     → campo mercadopago_plan_id en membership_products
 *
 * VIP DualCTA por país (stripePriceId en site_settings) → /admin/country-templates.
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { ConfigOwnerCallout } from "@/components/admin/ConfigOwnerCallout";
import {
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  Landmark,
  Loader2,
  Plus,
  RefreshCw,
  Link2,
  AlertCircle,
  Info,
  ArrowRight,
  Pencil,
} from "lucide-react";
import { BRAND_NAME } from "@/lib/brandConfig";

// ── The four membership levels — same for every brand ─────────────────────────
// Defined here as a constant (mirrors sql/032_user_memberships.sql seed).
// The UI always shows all four even if the DB is empty or levels table missing.

const LEVEL_DEFINITIONS = [
  {
    level_code: "basic",
    name: "Basic",
    name_es: "Básico",
    rank: 0,
    color: "#9CA3AF",
    emoji: "⚡",
    description_es: "Acceso básico — grupo de Telegram",
    benefits: ["Grupo de Telegram"],
  },
  {
    level_code: "standard",
    name: "Standard",
    name_es: "Estándar",
    rank: 1,
    color: "#C0C0C0",
    emoji: "⭐",
    description_es: "Acceso al grupo exclusivo de Telegram",
    benefits: ["Grupo exclusivo de Telegram"],
  },
  {
    level_code: "vip",
    name: "VIP",
    name_es: "VIP",
    rank: 2,
    color: "#F59E0B",
    emoji: "👑",
    description_es: "Telegram + Mejores Amigos IG + Contenido exclusivo",
    benefits: ["Telegram", "Mejores Amigos Instagram", "Contenido exclusivo", "Respuestas prioritarias"],
  },
  {
    level_code: "platinum",
    name: "Platinum",
    name_es: "Platino",
    rank: 3,
    color: "#8B5CF6",
    emoji: "💎",
    description_es: "Acceso premium + early access + mensajes directos",
    benefits: ["Todo VIP", "Acceso anticipado", "Mensajes directos"],
  },
] as const;

type LevelCode = (typeof LEVEL_DEFINITIONS)[number]["level_code"];

// ── Types ─────────────────────────────────────────────────────────────────────

interface MembershipProduct {
  id: string;
  name: string;
  slug: string;
  price_usd: number | null;
  price_ars: number | null;
  billing_interval: string | null;
  billing_interval_count: number | null;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  mercadopago_plan_id: string | null;
  is_active: boolean;
}

interface LevelConfig {
  success_redirect_url: string | null;
  success_redirect_label: string | null;
}

// Products indexed by level_code
type ProductsByLevel = Record<LevelCode, MembershipProduct[]>;
type ConfigByLevel = Record<LevelCode, LevelConfig>;

// ── Auth helper ───────────────────────────────────────────────────────────────

function getAdminToken(): string | null {
  return (
    (import.meta.env.VITE_ADMIN_DASHBOARD_API_KEY as string | undefined) ||
    localStorage.getItem("auth_token") ||
    sessionStorage.getItem("auth_token") ||
    null
  );
}

// ── Formatting helper ─────────────────────────────────────────────────────────

function intervalLabel(interval: string | null, count: number | null): string {
  if (!interval) return "";
  const c = count ?? 1;
  const base = interval === "monthly" || interval === "month" ? "mes" : "año";
  return c === 1 ? `/ ${base}` : `cada ${c} ${base}es`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stripe form — creates or links a Price for one level
// ─────────────────────────────────────────────────────────────────────────────

function StripePlanForm({
  levelCode,
  levelName,
  onCreated,
}: {
  levelCode: string;
  levelName: string;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"create" | "link">("create");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: `Membresía ${levelName}`,
    description: "",
    amount: "",
    currency: "usd",
    interval: "month" as "month" | "year",
    interval_count: 1,
    existing_price_id: "",
    existing_product_id: "",
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAdminToken();
    if (!token) {
      toast({ title: "Sin API key de admin", variant: "destructive" });
      return;
    }
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: "Monto inválido — debe ser > 0", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/admin/stripe-plans", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          amount: Math.round(amount * 100), // → centavos
          currency: form.currency,
          interval: form.interval,
          interval_count: form.interval_count,
          plan_tier: levelCode,
        }),
      });
      const data = (await res.json()) as { success?: boolean; price_id?: string; error?: string };
      if (!res.ok || !data.success) throw new Error(data.error ?? "Error al crear en Stripe");
      toast({ title: "✓ Plan Stripe creado", description: `price_id: ${data.price_id}` });
      onCreated();
    } catch (err) {
      toast({ title: "Error Stripe", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAdminToken();
    if (!token) {
      toast({ title: "Sin API key de admin", variant: "destructive" });
      return;
    }
    const priceId = form.existing_price_id.trim();
    if (!priceId.startsWith("price_")) {
      toast({ title: "Price ID inválido", description: "Debe empezar con price_", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const amount = parseFloat(form.amount) || 0;
      const res = await fetch("/api/admin/membership-products", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name.trim(),
          price_usd: amount,
          product_type: "recurring",
          product_category: "membership",
          billing_interval: form.interval === "month" ? "monthly" : "yearly",
          billing_interval_count: form.interval_count,
          plan_tier: levelCode,
          stripe_price_id: priceId,
          stripe_product_id: form.existing_product_id.trim() || null,
          is_active: true,
          visibility: "public",
        }),
      });
      const data = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok || !data.success) throw new Error(data.error ?? "Error al vincular");
      toast({ title: "✓ Price ID vinculado", description: priceId });
      onCreated();
    } catch (err) {
      toast({ title: "Error al vincular", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const f = form;
  const set = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <div className="space-y-3">
      {/* Mode tabs */}
      <div className="flex gap-1.5 p-1 bg-muted rounded-md w-fit">
        <button
          type="button"
          onClick={() => setMode("create")}
          className={`text-xs px-3 py-1 rounded transition-colors ${mode === "create" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Plus className="h-3 w-3 inline mr-1" />
          Crear nuevo en Stripe
        </button>
        <button
          type="button"
          onClick={() => setMode("link")}
          className={`text-xs px-3 py-1 rounded transition-colors ${mode === "link" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Link2 className="h-3 w-3 inline mr-1" />
          Vincular Price ID existente
        </button>
      </div>

      {mode === "create" && (
        <form onSubmit={handleCreate} className="space-y-3">
          <div className="grid grid-cols-2 gap-2.5">
            <div className="col-span-2">
              <Label className="text-xs">Nombre del plan</Label>
              <Input
                value={f.name}
                onChange={(e) => set({ name: e.target.value })}
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Monto *</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="9.99"
                value={f.amount}
                onChange={(e) => set({ amount: e.target.value })}
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Moneda</Label>
              <Select value={f.currency} onValueChange={(v) => set({ currency: v })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="usd">USD</SelectItem>
                  <SelectItem value="eur">EUR</SelectItem>
                  <SelectItem value="brl">BRL</SelectItem>
                  <SelectItem value="mxn">MXN</SelectItem>
                  <SelectItem value="clp">CLP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cobro cada</Label>
              <div className="flex gap-1.5">
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={f.interval_count}
                  onChange={(e) => set({ interval_count: parseInt(e.target.value) || 1 })}
                  className="h-8 text-sm w-16"
                />
                <Select value={f.interval} onValueChange={(v: "month" | "year") => set({ interval: v })}>
                  <SelectTrigger className="h-8 text-sm flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">mes(es)</SelectItem>
                    <SelectItem value="year">año(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={loading} className="h-8 gap-1.5 text-xs">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Crear en Stripe
            </Button>
          </div>
        </form>
      )}

      {mode === "link" && (
        <form onSubmit={handleLink} className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Pega el Price ID de un plan que ya existe en tu cuenta de Stripe.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="col-span-2">
              <Label className="text-xs">Nombre del plan</Label>
              <Input value={f.name} onChange={(e) => set({ name: e.target.value })} className="h-8 text-sm" required />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Stripe Price ID *</Label>
              <Input
                value={f.existing_price_id}
                onChange={(e) => set({ existing_price_id: e.target.value })}
                placeholder="price_1ABC..."
                className="h-8 text-sm font-mono"
                required
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Stripe Product ID (opcional)</Label>
              <Input
                value={f.existing_product_id}
                onChange={(e) => set({ existing_product_id: e.target.value })}
                placeholder="prod_1ABC..."
                className="h-8 text-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Precio USD (referencia)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={f.amount}
                onChange={(e) => set({ amount: e.target.value })}
                placeholder="9.99"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Intervalo</Label>
              <Select value={f.interval} onValueChange={(v: "month" | "year") => set({ interval: v })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Mensual</SelectItem>
                  <SelectItem value="year">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={loading} className="h-8 gap-1.5 text-xs">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
              Vincular
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// init_point result card shown after MP plan creation
// ─────────────────────────────────────────────────────────────────────────────

function CreatedPlanResult({ planId, initPoint }: { planId: string; initPoint: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(initPoint).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
        <span className="text-sm font-semibold text-emerald-300">Plan creado en MercadoPago</span>
        <code className="text-[10px] text-emerald-300/50 ml-auto font-mono">{planId}</code>
      </div>
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">Link de suscripción (init_point):</p>
        <code className="block text-xs text-foreground/70 break-all bg-black/30 px-2.5 py-2 rounded-lg border border-border/20 leading-relaxed">
          {initPoint}
        </code>
        <div className="flex gap-2">
          <a
            href={initPoint}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-lg border border-border/40 bg-white/5 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            Abrir
          </a>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1.5 rounded-lg border border-border/40 bg-white/5 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copiado" : "Copiar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MercadoPago form — creates or links a preapproval_plan for one level
// ─────────────────────────────────────────────────────────────────────────────

function MercadoPagoPlanForm({
  levelCode,
  levelName,
  onCreated,
}: {
  levelCode: string;
  levelName: string;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"create" | "link">("create");
  const [loading, setLoading] = useState(false);
  const [createdResult, setCreatedResult] = useState<{ id: string; init_point: string } | null>(null);
  const [form, setForm] = useState({
    reason: `Membresía ${levelName}`,
    amount: "",
    frequency: 1,
    frequencyType: "months" as "months" | "days",
    billingDay: 0,
    freeTrialMonths: 0,
    existing_plan_id: "",
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatedResult(null);
    const token = getAdminToken();
    if (!token) {
      toast({ title: "Sin API key de admin", variant: "destructive" });
      return;
    }
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: "Monto ARS inválido — debe ser > 0", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        reason: form.reason.trim(),
        amount,
        currency: "ARS",
        frequency: form.frequency,
        frequencyType: form.frequencyType,
        // flow must be "bio_template_premium" so getPlanId() in create-subscription.ts finds it.
        // The API also saves a copy under "admin_dashboard" for traceability.
        metadata: { tier: levelCode, flow: "bio_template_premium" },
      };
      if (form.billingDay > 0) payload.billingDay = form.billingDay;
      if (form.freeTrialMonths > 0) {
        payload.freeTrialFrequency = form.freeTrialMonths;
        payload.freeTrialFrequencyType = "months";
      }
      const res = await fetch("/api/mercadopago/create-preapproval-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as { id?: string; init_point?: string; error?: string };
      if (!res.ok || !data.id) throw new Error(data.error ?? "Error al crear en MercadoPago");
      setCreatedResult({ id: data.id, init_point: data.init_point ?? "" });
      toast({ title: "✓ Plan MercadoPago creado", description: `plan_id: ${data.id}` });
      onCreated();
    } catch (err) {
      toast({ title: "Error MercadoPago", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAdminToken();
    if (!token) {
      toast({ title: "Sin API key de admin", variant: "destructive" });
      return;
    }
    const planId = form.existing_plan_id.trim();
    if (!planId) {
      toast({ title: "Plan ID requerido", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const amount = parseFloat(form.amount) || 0;
      const res = await fetch("/api/admin/membership-products", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.reason.trim(),
          price_usd: 0,
          price_ars: amount > 0 ? amount : null,
          product_type: "recurring",
          product_category: "membership",
          billing_interval: "monthly",
          billing_interval_count: form.frequency,
          plan_tier: levelCode,
          mercadopago_plan_id: planId, // ← SOLO campo de MercadoPago
          is_active: true,
          visibility: "public",
        }),
      });
      const data = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok || !data.success) throw new Error(data.error ?? "Error al vincular");
      toast({ title: "✓ Plan ID vinculado", description: planId });
      onCreated();
    } catch (err) {
      toast({ title: "Error al vincular", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const f = form;
  const set = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <div className="space-y-3">
      {/* Mode tabs */}
      <div className="flex gap-1.5 p-1 bg-muted rounded-md w-fit">
        <button
          type="button"
          onClick={() => setMode("create")}
          className={`text-xs px-3 py-1 rounded transition-colors ${mode === "create" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Plus className="h-3 w-3 inline mr-1" />
          Crear preapproval_plan en MP
        </button>
        <button
          type="button"
          onClick={() => setMode("link")}
          className={`text-xs px-3 py-1 rounded transition-colors ${mode === "link" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Link2 className="h-3 w-3 inline mr-1" />
          Vincular Plan ID existente
        </button>
      </div>

      {mode === "create" && (
        <form onSubmit={handleCreate} className="space-y-3">
          <div className="grid grid-cols-2 gap-2.5">
            <div className="col-span-2">
              <Label className="text-xs">Nombre del plan (reason) *</Label>
              <Input
                value={f.reason}
                onChange={(e) => set({ reason: e.target.value })}
                placeholder={`Membresía ${levelName} Argentina`}
                className="h-8 text-sm"
                required
              />
              <p className="text-xs text-muted-foreground mt-0.5">Aparece en el checkout de MercadoPago</p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Monto en ARS *</Label>
              <Input
                type="number"
                min="1"
                step="1"
                placeholder="5000"
                value={f.amount}
                onChange={(e) => set({ amount: e.target.value })}
                className="h-8 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Cobro cada</Label>
              <div className="flex gap-1.5">
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={f.frequency}
                  onChange={(e) => set({ frequency: parseInt(e.target.value) || 1 })}
                  className="h-8 text-sm w-16"
                />
                <Select
                  value={f.frequencyType}
                  onValueChange={(v: "months" | "days") => set({ frequencyType: v })}
                >
                  <SelectTrigger className="h-8 text-sm flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="months">mes(es)</SelectItem>
                    <SelectItem value="days">día(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Prueba gratis (meses, 0 = sin prueba)</Label>
              <Input
                type="number"
                min="0"
                max="12"
                value={f.freeTrialMonths || ""}
                onChange={(e) => set({ freeTrialMonths: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={loading} className="h-8 gap-1.5 text-xs">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Crear en MercadoPago
            </Button>
          </div>
        </form>
      )}

      {createdResult && createdResult.init_point && (
        <CreatedPlanResult
          planId={createdResult.id}
          initPoint={createdResult.init_point}
        />
      )}

      {mode === "link" && (
        <form onSubmit={handleLink} className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Pega el ID de un preapproval_plan ya existente en tu cuenta de MercadoPago.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="col-span-2">
              <Label className="text-xs">Nombre del plan</Label>
              <Input value={f.reason} onChange={(e) => set({ reason: e.target.value })} className="h-8 text-sm" required />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">MercadoPago Plan ID *</Label>
              <Input
                value={f.existing_plan_id}
                onChange={(e) => set({ existing_plan_id: e.target.value })}
                placeholder="2c938084..."
                className="h-8 text-sm font-mono"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Monto ARS (referencia)</Label>
              <Input
                type="number"
                min="0"
                value={f.amount}
                onChange={(e) => set({ amount: e.target.value })}
                placeholder="5000"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Frecuencia</Label>
              <Input
                type="number"
                min="1"
                value={f.frequency}
                onChange={(e) => set({ frequency: parseInt(e.target.value) || 1 })}
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={loading} className="h-8 gap-1.5 text-xs">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
              Vincular
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Success URL form — configure redirect per level
// ─────────────────────────────────────────────────────────────────────────────

function SuccessUrlForm({
  levelCode,
  currentUrl,
  currentLabel,
  onSaved,
}: {
  levelCode: string;
  currentUrl: string | null;
  currentLabel: string | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(!currentUrl);
  const [url, setUrl] = useState(currentUrl ?? "");
  const [label, setLabel] = useState(currentLabel ?? "");

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAdminToken();
    if (!token) {
      toast({ title: "Sin API key de admin", variant: "destructive" });
      return;
    }
    if (!url.trim().startsWith("http")) {
      toast({ title: "URL inválida", description: "Debe comenzar con http:// o https://", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/admin/membership-level-config", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          level_code: levelCode,
          success_redirect_url: url.trim() || null,
          success_redirect_label: label.trim() || null,
        }),
      });
      const data = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok || !data.success) throw new Error(data.error ?? "Error al guardar");
      toast({ title: "✓ URL de éxito guardada" });
      setEditing(false);
      onSaved();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (!editing && currentUrl) {
    return (
      <div className="flex items-center gap-2 flex-wrap text-xs p-2 rounded-md bg-muted/50 border">
        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs bg-background px-1.5 py-0.5 rounded border hover:underline flex items-center gap-1 max-w-[280px] truncate"
        >
          {currentUrl}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        {currentLabel && (
          <span className="text-muted-foreground italic">"{currentLabel}"</span>
        )}
        <button
          type="button"
          onClick={() => { setUrl(currentUrl); setLabel(currentLabel ?? ""); setEditing(true); }}
          className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Pencil className="h-3 w-3" />
          Editar
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-2">
      <div>
        <Label className="text-xs">URL de destino *</Label>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://t.me/+XXXXXXXX  o  https://fan.brand.com/registro"
          className="h-8 text-sm font-mono"
          required
        />
        <p className="text-xs text-muted-foreground mt-0.5">
          Para Telegram: copia el link de invitación del grupo. Para VIP: URL del portal de registro.
        </p>
      </div>
      <div>
        <Label className="text-xs">Texto del botón (opcional)</Label>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='ej. "Unirme al grupo", "Ir al portal exclusivo"'
          className="h-8 text-sm"
        />
        <p className="text-xs text-muted-foreground mt-0.5">
          Si se deja vacío, se usa el texto por defecto según el idioma del usuario.
        </p>
      </div>
      <div className="flex gap-2 justify-end">
        {currentUrl && (
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        )}
        <Button type="submit" size="sm" disabled={loading} className="h-7 gap-1.5 text-xs">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          Guardar URL
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Configured plan row (read-only display)
// ─────────────────────────────────────────────────────────────────────────────

function ConfiguredPlans({
  products,
  provider,
}: {
  products: MembershipProduct[];
  provider: "stripe" | "mercadopago";
}) {
  const filtered = products.filter((p) =>
    provider === "stripe" ? p.stripe_price_id : p.mercadopago_plan_id
  );
  if (filtered.length === 0) return null;

  return (
    <div className="space-y-1.5 mb-3">
      {filtered.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-2 flex-wrap text-xs p-2 rounded-md bg-muted/50 border"
        >
          <CheckCircle2
            className={`h-3.5 w-3.5 shrink-0 ${provider === "stripe" ? "text-green-500" : "text-blue-500"}`}
          />
          <code className="font-mono text-xs bg-background px-1.5 py-0.5 rounded border">
            {provider === "stripe" ? p.stripe_price_id : p.mercadopago_plan_id}
          </code>
          {provider === "stripe" && p.price_usd != null && (
            <span className="text-muted-foreground">
              USD ${Number(p.price_usd).toFixed(2)}{" "}
              {intervalLabel(p.billing_interval, p.billing_interval_count)}
            </span>
          )}
          {provider === "mercadopago" && p.price_ars != null && (
            <span className="text-muted-foreground">
              ARS ${Number(p.price_ars).toLocaleString()}{" "}
              {intervalLabel(p.billing_interval, p.billing_interval_count)}
            </span>
          )}
          <Badge
            variant={p.is_active ? "default" : "secondary"}
            className="text-xs py-0 ml-auto"
          >
            {p.is_active ? "activo" : "inactivo"}
          </Badge>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Level card
// ─────────────────────────────────────────────────────────────────────────────

function LevelCard({
  level,
  products,
  config,
  onRefresh,
}: {
  level: (typeof LEVEL_DEFINITIONS)[number];
  products: MembershipProduct[];
  config: LevelConfig;
  onRefresh: () => void;
}) {
  const hasStripe = products.some((p) => p.stripe_price_id);
  const hasMP = products.some((p) => p.mercadopago_plan_id);
  const hasSuccessUrl = Boolean(config.success_redirect_url);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-0 pt-4 px-4">
        <div className="flex items-start justify-between gap-3">
          {/* Level identity */}
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-lg shrink-0 shadow-sm"
              style={{ backgroundColor: level.color + "22", border: `2px solid ${level.color}` }}
            >
              {level.emoji}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{level.name_es}</span>
                <code className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                  {level.level_code}
                </code>
                <span className="text-xs text-muted-foreground">rank {level.rank}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{level.description_es}</p>
              {/* Benefits */}
              <div className="flex flex-wrap gap-1 mt-1">
                {level.benefits.map((b) => (
                  <Badge key={b} variant="secondary" className="text-xs py-0 font-normal">
                    {b}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Status badges */}
          <div className="flex flex-col gap-1 items-end shrink-0">
            <Badge className={`text-xs gap-1 border-0 ${hasStripe ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" : "bg-muted text-muted-foreground"}`}>
              <CreditCard className="h-3 w-3" />Stripe {hasStripe ? "✓" : "—"}
            </Badge>
            <Badge className={`text-xs gap-1 border-0 ${hasMP ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" : "bg-muted text-muted-foreground"}`}>
              <Landmark className="h-3 w-3" />MP {hasMP ? "✓" : "—"}
            </Badge>
            <Badge className={`text-xs gap-1 border-0 ${hasSuccessUrl ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" : "bg-muted text-muted-foreground"}`}>
              <ArrowRight className="h-3 w-3" />Redirect {hasSuccessUrl ? "✓" : "—"}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pt-3 pb-4">
        <Separator className="mb-3" />
        <Accordion type="multiple" className="space-y-2">
          {/* ── Stripe ── */}
          <AccordionItem value="stripe" className="border rounded-lg px-3">
            <AccordionTrigger className="py-2.5 hover:no-underline">
              <div className="flex items-center gap-2 text-sm">
                <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">Stripe</span>
                <span className="text-xs text-muted-foreground font-normal">
                  — USD · internacional · <code className="bg-muted px-1 rounded">stripe_price_id</code>
                </span>
                {hasStripe && <CheckCircle2 className="h-3.5 w-3.5 text-green-500 ml-auto mr-2" />}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-3 space-y-2">
              {hasStripe && (
                <>
                  <ConfiguredPlans products={products} provider="stripe" />
                  <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 mb-1">
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    El checkout de Stripe usa este <code>stripe_price_id</code> automáticamente (sin descuento ni trimestral).
                  </div>
                  <p className="text-xs text-muted-foreground">Agregar otro plan Stripe para este nivel:</p>
                </>
              )}
              {!hasStripe && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-2">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Sin plan configurado. El checkout usará el precio del env var <code>VITE_PREMIUM_USD_*</code> como fallback.
                </div>
              )}
              <StripePlanForm
                levelCode={level.level_code}
                levelName={level.name_es}
                onCreated={onRefresh}
              />
            </AccordionContent>
          </AccordionItem>

          {/* ── MercadoPago ── */}
          <AccordionItem value="mercadopago" className="border rounded-lg px-3">
            <AccordionTrigger className="py-2.5 hover:no-underline">
              <div className="flex items-center gap-2 text-sm">
                <Landmark className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">MercadoPago</span>
                <span className="text-xs text-muted-foreground font-normal">
                  — ARS · Argentina · <code className="bg-muted px-1 rounded">mercadopago_plan_id</code>
                </span>
                {hasMP && <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 ml-auto mr-2" />}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-3 space-y-2">
              {hasMP && (
                <>
                  <ConfiguredPlans products={products} provider="mercadopago" />
                  <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 mb-1">
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    El checkout de MercadoPago busca este plan por <code>flow_name=bio_template_premium</code> + <code>plan_tier</code>.
                  </div>
                  <p className="text-xs text-muted-foreground">Agregar otro plan MercadoPago para este nivel:</p>
                </>
              )}
              {!hasMP && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-2">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Sin plan configurado. El checkout usará el precio del env var <code>VITE_PREMIUM_ARS_*</code> como fallback.
                </div>
              )}
              <MercadoPagoPlanForm
                levelCode={level.level_code}
                levelName={level.name_es}
                onCreated={onRefresh}
              />
            </AccordionContent>
          </AccordionItem>

          {/* ── Success redirect URL ── */}
          <AccordionItem value="success-url" className="border rounded-lg px-3">
            <AccordionTrigger className="py-2.5 hover:no-underline">
              <div className="flex items-center gap-2 text-sm">
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">URL de éxito post-pago</span>
                <span className="text-xs text-muted-foreground font-normal">
                  — redirect tras pago aprobado
                </span>
                {hasSuccessUrl && <CheckCircle2 className="h-3.5 w-3.5 text-purple-500 ml-auto mr-2" />}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                URL a la que se redirige al usuario en la página de éxito tras un pago aprobado de{" "}
                <strong>cualquier proveedor</strong> (Stripe y MercadoPago).
                Típicamente es un link de invitación a Telegram o el portal VIP del brand.
              </p>
              {!hasSuccessUrl && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-1">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Sin URL configurada. La página de éxito mostrará un mensaje genérico hasta que se configure.
                </div>
              )}
              <SuccessUrlForm
                levelCode={level.level_code}
                currentUrl={config.success_redirect_url}
                currentLabel={config.success_redirect_label}
                onSaved={onRefresh}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function SubscripcionesPlanes() {
  const { toast } = useToast();

  // Payment products indexed by level_code — fetched from DB
  const [productsByLevel, setProductsByLevel] = useState<ProductsByLevel>({
    basic: [],
    standard: [],
    vip: [],
    platinum: [],
  });
  const [configByLevel, setConfigByLevel] = useState<ConfigByLevel>({
    basic:    { success_redirect_url: null, success_redirect_label: null },
    standard: { success_redirect_url: null, success_redirect_label: null },
    vip:      { success_redirect_url: null, success_redirect_label: null },
    platinum: { success_redirect_url: null, success_redirect_label: null },
  });
  const [loading, setLoading] = useState(true);
  const [dbSource, setDbSource] = useState<"database" | "static" | null>(null);

  const fetchPlans = useCallback(async () => {
    const token = getAdminToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/admin/membership-levels", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await res.json()) as {
        success?: boolean;
        source?: "database" | "static";
        levels?: Array<{
          level_code: string;
          products: MembershipProduct[];
          success_redirect_url?: string | null;
          success_redirect_label?: string | null;
        }>;
        error?: string;
      };
      if (!res.ok || !data.success) throw new Error(data.error ?? "Error al cargar planes");

      setDbSource(data.source ?? "static");

      // Build productsByLevel and configByLevel maps
      const map: ProductsByLevel = { basic: [], standard: [], vip: [], platinum: [] };
      const cfg: ConfigByLevel = {
        basic:    { success_redirect_url: null, success_redirect_label: null },
        standard: { success_redirect_url: null, success_redirect_label: null },
        vip:      { success_redirect_url: null, success_redirect_label: null },
        platinum: { success_redirect_url: null, success_redirect_label: null },
      };
      for (const lvl of data.levels ?? []) {
        if (lvl.level_code in map) {
          map[lvl.level_code as LevelCode] = (lvl.products ?? []) as MembershipProduct[];
          cfg[lvl.level_code as LevelCode] = {
            success_redirect_url: lvl.success_redirect_url ?? null,
            success_redirect_label: lvl.success_redirect_label ?? null,
          };
        }
      }
      setProductsByLevel(map);
      setConfigByLevel(cfg);
    } catch (err) {
      toast({
        title: "Error al cargar planes",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  // How many levels have at least one payment plan configured
  const configured = LEVEL_DEFINITIONS.filter(
    (l) => productsByLevel[l.level_code].length > 0
  ).length;
  const isNewBrand = configured === 0 && !loading;

  return (
    <AdminPageShell width="sm" className="space-y-5">
      <AdminPageHeader
        description={
          <>
            Catálogo <strong>{BRAND_NAME}</strong> por nivel. Solo se usa si el país{" "}
            <strong>no</strong> tiene price_ en Mux / país.
          </>
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPlans}
            disabled={loading}
            className="gap-1.5 shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        }
      />

      <ConfigOwnerCallout
        variant="fallback"
        owns="membership_products.stripe_price_id por plan_tier (fallback VIP global)."
        notHere="No edites acá el Price de un país DualCTA: country stripePriceId / VIP default gana siempre."
        ownerPath="/admin/country-templates"
        ownerLabel="Home por país — Crear Price en Stripe"
        priority={[
          "1) country-templates: vip_pricing_by_country[ISO2] o VIP default compacto (+ promo_)",
          "2) resolveInternationalHomeOffer FALLBACK price_ + ANA70 (si mapa vacío)",
          "3) membership_products VIP (esta page) — solo path sin DualCTA/price explícito",
          "4) Server STRIPE_FALLBACK_VIP_PRICE_ID",
        ]}
      />

      {/* ── How it connects to the checkout ── */}
      <div className="rounded-lg border border-muted bg-muted/30 p-3 space-y-2 text-xs text-muted-foreground">
        <p className="font-semibold text-foreground text-sm flex items-center gap-1.5">
          <Info className="h-4 w-4" />
          Cómo conecta con el checkout
        </p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          <div className="flex items-start gap-1.5">
            <CreditCard className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              <strong className="text-foreground">Stripe:</strong> el <code>stripe_price_id</code>{" "}
              guardado aquí se usa en <code>/api/create-subscription-checkout</code>.
              Si no hay Price ID, el checkout crea el precio inline desde el env var{" "}
              <code>VITE_PREMIUM_USD_*</code>.
            </span>
          </div>
          <div className="flex items-start gap-1.5">
            <Landmark className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              <strong className="text-foreground">MercadoPago:</strong> el plan creado aquí se
              registra con <code>flow_name = bio_template_premium</code> en la tabla{" "}
              <code>mercadopago_plans</code>. El checkout lo busca por{" "}
              <code>(flow, plan_tier)</code> vía <code>getPlanId()</code>.
            </span>
          </div>
        </div>
      </div>

      {/* ── New brand banner ── */}
      {isNewBrand && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-900 p-4 flex gap-3">
          <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-semibold text-blue-800 dark:text-blue-300">
              Setup inicial — sin planes configurados todavía
            </p>
            <p className="text-blue-700 dark:text-blue-400 text-xs">
              Los niveles de membresía ya están definidos en el sistema (basic → standard → vip → platinum).
              Crea o vincula los planes de pago de{" "}
              <strong>Stripe</strong> (USD · internacional) y{" "}
              <strong>MercadoPago</strong> (ARS · Argentina) para cada nivel.
              Una vez guardados, el checkout los usará automáticamente.
            </p>
          </div>
        </div>
      )}

      {/* ── DB source note ── */}
      {dbSource === "static" && !isNewBrand && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 flex gap-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            La tabla <code>membership_levels</code> no existe aún en la base de datos.
            Los niveles mostrados son los valores por defecto del sistema. Ejecuta las migraciones para crear la tabla.
          </span>
        </div>
      )}

      {/* ── Setup progress ── */}
      {!loading && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground rounded-md border px-3 py-2.5 bg-muted/20">
          <div className="flex gap-1">
            {LEVEL_DEFINITIONS.map((l) => {
              const hasAny = productsByLevel[l.level_code].length > 0;
              return (
                <div
                  key={l.level_code}
                  title={`${l.name_es}: ${hasAny ? "configurado" : "pendiente"}`}
                  className={`h-2 w-8 rounded-full transition-colors ${hasAny ? "bg-green-500" : "bg-muted-foreground/20"}`}
                />
              );
            })}
          </div>
          <span>
            {configured}/{LEVEL_DEFINITIONS.length} niveles con planes configurados
          </span>
          <span className="ml-auto flex items-center gap-3">
            <a
              href="https://dashboard.stripe.com/products"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 hover:underline"
            >
              <CreditCard className="h-3 w-3" />
              Stripe Dashboard <ExternalLink className="h-2.5 w-2.5" />
            </a>
            <a
              href="https://www.mercadopago.com/developers/panel"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 hover:underline"
            >
              <Landmark className="h-3 w-3" />
              MP Panel <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </span>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* ── Level cards — always shown, even for new brand ── */}
      {!loading && (
        <div className="space-y-4">
          {LEVEL_DEFINITIONS.map((level) => (
            <LevelCard
              key={level.level_code}
              level={level}
              products={productsByLevel[level.level_code]}
              config={configByLevel[level.level_code]}
              onRefresh={fetchPlans}
            />
          ))}
        </div>
      )}
    </AdminPageShell>
  );
}
